import type { Language } from './landingCopy';

/*
  Words for www.city-seek.com/coupons: CitySeek Coupons, pitched to the places
  that give the offers (cafés, bars, restaurants, activities, shops).

  Money rule (owner, 2026-10-10): this page never names what a business pays,
  not a number and not the pricing model. It promises only what is true for
  every partner: no setup fee, no upfront payment, no ad budget, a monthly
  limit they set, and that they can stop. Terms come in person, after the form.

  German uses „…“, English and Turkish “…”. No em-dashes anywhere.
*/

/* Who sent the form. The ids travel to the server as `businessType`. */
export const BUSINESS_TYPES = ['cafe', 'bar', 'restaurant', 'club', 'activity', 'culture', 'shop', 'sweets', 'other'] as const;
export type BusinessType = (typeof BUSINESS_TYPES)[number];

/*
  Partners with live coupons on 2026-10-10. The page shows these until the
  live list from the API arrives, so the prerendered page is never empty.
*/
export const PARTNERS_FALLBACK = ['Café Fräulein Mondschein', 'Café Bellevue', "Berry's Café-Bar", 'Seydioğlu Baklava', 'Lowbrow × Red Barn'];

/* The proof numbers, the same ones the landing page shows (landingCopy STATS). */
export const COUPON_STATS = { places: '39', events: '30+', visits: '700+', languages: '3' };

/*
  What the partner form sends as `sequence` and `perPersonLimit`, in the order
  the form shows them. '10' is a fixed 10% on every coupon, with no step-up;
  the limit per person still applies to it like to the step-ups.
*/
export const SEQUENCES = ['10-15', '10-15-20', '10'] as const;
export type Sequence = (typeof SEQUENCES)[number];
export const PER_PERSON_LIMITS = ['none', 'day', 'week', 'month'] as const;
export type PerPersonLimit = (typeof PER_PERSON_LIMITS)[number];

type Pair = [string, string];
type Triple = [string, string, string];

export const couponsCopy = {
 en: {
  title: 'CitySeek Coupons for Local Businesses in Darmstadt',
  description: 'New guests for your café, bar, restaurant or activity in Darmstadt. No setup fee, no upfront payment, no ad budget. CitySeek users come to you.',
  ogAlt: 'Guests at the door of the CitySeek pop-up in Darmstadt',
  skip: 'Skip to content',
  menu: 'Menu', close: 'Close', languages: 'Language',
  nav: ['How it works', 'Your offer', 'Who it’s for', 'Questions'],
  navCta: 'Become a partner',
  forEveryone: 'CitySeek for everyone',

  heroLabel: 'CitySeek Coupons · for local businesses',
  headline: ['New faces at', 'your counter.'],
  intro: 'CitySeek users earn coins on city walks and at local events. They swap those coins for coupons at local places. Put your offer on the map, and they come to you.',
  primary: 'Become a partner',
  secondary: 'See how it works',
  promises: ['No setup fee', 'No upfront payment', 'No ad budget', 'Pause any time'],
  heroPhotoAlt: 'Guests at the door of the CitySeek pop-up on Luisenplatz, Darmstadt',
  heroPhoneAlt: 'The CitySeek Coupons list in the app: free drinks and cake at Darmstadt cafés',
  scanTitle: 'Coupon scanned',
  scanLine: 'Free drink · 1 new guest',
  coinsChip: 'Earned on a city walk',

  liveLabel: 'Already on the coupon map',
  liveCount: '{c} coupons at {p} places in Darmstadt, live right now',
  liveLink: 'Open the coupon map',

  whyLabel: 'Why places join',
  whyTitle: 'Real people through your door, not clicks on a screen.',
  why: [
   ['Guests, not clicks', 'Every coupon is used in person. Your team scans a QR code at the counter, so you see every visit.'],
   ['People who are already out', 'Our users are out in the city: on a walk, on the way to an event, looking for the next stop. Your place is on their way.'],
   ['First visits that turn into regulars', 'With “Come back again”, the same coupon gets better for the same person. The better deal only comes after they used a coupon at your place.'],
   ['Quiet hours, filled', 'You pick the days and times. Cake on a slow afternoon, a drink early in the week, a free game on a quiet Tuesday.'],
   ['You stay in control', 'You decide the offer, how many coupons run each month and for how long. Change or pause it any time.'],
   ['Seen in three languages', 'Your offer shows in German, English and Turkish. Students, internationals and newcomers find you as easily as locals.'],
  ] as Pair[],

  howLabel: 'How it works',
  howTitle: 'From a city walk to your counter',
  steps: [
   ['They explore', 'Users walk CitySeek routes through Darmstadt, hear a story at each stop and join community events.'],
   ['They earn coins', 'Coins only come from walking and taking part. Nobody can buy them.'],
   ['They find you', 'Your coupon is on the CitySeek map with your name, address and photo. They unlock it and come over.'],
   ['You scan, done', 'Any phone camera reads the QR code. One tap confirms it. No extra device, no new till.'],
  ] as Pair[],

  seeLabel: 'What your guests see',
  seeTitle: 'Your place, right where people plan their day.',
  seeText: 'CitySeek Coupons is part of the CitySeek app, next to events and city walks. People open it when they decide where to go next.',
  seePoints: [
   ['A coupon with your name', 'Your photo, your address and your offer, easy to read at a glance.'],
   ['A pin on the coupon map', 'People nearby see where you are and how far it is to walk.'],
   ['Clear colours', 'Free treats are green, discounts orange, return visits violet.'],
  ] as Pair[],
  shotList: 'Coupon list',
  shotMap: 'Coupon map',
  shotMapAlt: 'The CitySeek coupon map of Darmstadt with partner pins',

  offerLabel: 'Your offer',
  offerTitle: 'Three ways to welcome new guests',
  offerIntro: 'Choose one of our two standard offers or tell us your own idea. You decide how many coupons run each month, and we set it up with you.',
  gainLabel: 'What you get',
  standardTag: 'Standard',
  pairName: 'Bring a friend',
  pairBadge: 'Best for small baskets',
  pairVisual: [['1st', 'full price'], ['2nd', 'half price']],
  pairText: 'Two people, one coupon. They order two of the same thing, for example two coffees. The second one is half price, or free if you prefer. If the prices differ, the cheaper one gets the discount.',
  pairRules: ['One coupon, one order of two', 'Scanned once, when they order', 'You choose what counts: hot drinks, cocktails, mains or tickets', 'You choose the days, times and monthly number'],
  pairGain: 'Two guests for one coupon. The first item is paid in full, and the friend is often a new face.',
  seriesName: 'Come back again',
  seriesBadge: 'Best for meals',
  seriesLadder: [['10%', '1st visit', 'Their first coupon'], ['15%', 'After a used coupon', 'Their next coupon'], ['20%', 'Then', 'It stays at 20%']] as Triple[],
  seriesText: 'The same coupon gets better for the same person. The first time they pay with 10% off. After that visit, their next coupon is 15%, then 20%. From then on it stays at 20%.',
  seriesShort: 'Or two steps: 10% → 15%. From then on it stays at 15%.',
  seriesFixed: 'Or keep it simple: a fixed 10%, every time.',
  seriesRules: ['Best where a percentage is real money: meals, bars, bigger bills'],
  seriesGain: 'A first visit, then a reason to come back. That is how a stranger becomes a regular.',
  ownTag: 'Custom',
  ownName: 'Your own offer',
  ownText: 'Anything that suits your place. Link it to something people buy, so it is always clear at the till.',
  ownExamples: ['A free cookie with any hot drink', 'Free dessert with a main', 'Half-price cake, Mon to Thu, 14:00 to 17:00', '4 play, 3 pay on weekdays', 'First hour free in a craft studio', 'A free topping or upgrade'],
  ownGain: 'An offer that fits your prices and fills the hours you choose.',
  tipTitle: 'Good to know',
  tipText: 'On a €4 coffee, 10% is only 40 cents. Nobody crosses town for that. A free extra or a second drink for a friend feels much bigger and still costs you little. Percentages work better for meals and bigger bills.',
  exampleTitle: 'Example: filled in by a café',
  example: [['Offer', 'Bring a friend: second hot drink half price'], ['When', 'Mon to Thu, 14:00 to 17:00'], ['Per month', 'up to 400 coupons'], ['Per person', '1 coupon per month']] as Pair[],

  limitLabel: 'Limit per person',
  limitTitle: 'No limit by default. Or one per day, week or month.',
  limitText: 'No limit: users can get your coupon again whenever they have enough coins. With a limit, each person gets one coupon per day, week or month.',
  limitScale: [['No limit', 'Default'], ['1 per person per day', ''], ['1 per person per week', ''], ['1 per person per month', '']] as Pair[],
  limitSeries: 'With “Come back again” steps and a limit, the better deal is waiting for them when the next day, week or month starts.',
  comboRule: 'One coupon per payment. Coupons cannot be combined.',
  coinRule: 'CitySeek sets the coin price for each step, in line with the other coupons on the map.',

  forLabel: 'Who it’s for',
  forTitle: 'Made for places people love to walk into',
  forHint: 'Tap yours to start the form.',
  types: {
   cafe: ['Cafés and bakeries', 'A coffee, a pastry, a slice of cake'],
   bar: ['Bars and pubs', 'A first round or the house special'],
   restaurant: ['Restaurants', 'A starter, a dessert or a lunch deal'],
   club: ['Clubs and live music', 'Early free entry or a welcome drink'],
   activity: ['Activities', 'Climbing, escape rooms, bowling, laser tag'],
   culture: ['Culture and tours', 'Cinema, museums, guided walks'],
   shop: ['Shops and makers', 'Bookshops, concept stores, local brands'],
   sweets: ['Ice cream and sweets', 'A scoop, a baklava, something sweet'],
   other: ['Something else', 'Tell us about your place'],
  } as Record<BusinessType, Pair>,

  compareLabel: 'Compared with other channels',
  compareTitle: 'The channel that sends guests, not reach',
  compareCols: ['Delivery apps', 'Deal sites', 'Online ads', 'CitySeek'],
  compareRows: [
   ['Guests come to your place', 'no', 'yes', 'partly', 'yes'],
   ['Every visit confirmed at the counter', 'no', 'partly', 'no', 'yes'],
   ['You set a monthly limit', 'no', 'partly', 'yes', 'yes'],
   ['Rewards the same guest for coming back', 'no', 'no', 'partly', 'yes'],
   ['Reaches people already out in the city', 'no', 'no', 'partly', 'yes'],
  ] as [string, string, string, string, string][],
  compareWords: { yes: 'Yes', partly: 'Partly', no: 'No' },
  compareNote: 'Partly means possible with extra effort or extra spend, not built in.',

  communityLabel: 'More than a coupon',
  communityTitle: 'We come by, together.',
  communityText: 'CitySeek is a community first. On our meetups and walks we visit partner places as a group, so a new spot quickly becomes a shared favourite. We run live music, breakfasts and pop-ups all over Darmstadt, and we bring people with us.',
  stats: [[COUPON_STATS.events, 'community events in Darmstadt'], [COUPON_STATS.visits, 'visits to our events'], [COUPON_STATS.places, 'places with a story on our routes'], [COUPON_STATS.languages, 'languages: Deutsch, English, Türkçe']] as Pair[],
  gardenAlt: 'Live music with saz and cello at a CitySeek event in a garden greenhouse',
  insideAlt: 'Inside the CitySeek pop-up on Luisenplatz',

  faqTitle: 'Questions venues ask us',
  faqs: [
   ['What does it cost?', 'No setup fee, no upfront payment, no ad budget. Before your coupon goes live, we agree simple and fair terms with you in person. You decide how many coupons run each month, so there are no surprises.'],
   ['Do my staff need an app or a new device?', 'No. Any phone with a camera reads the QR code and opens the coupon. One tap marks it as used.'],
   ['Who uses CitySeek?', 'Locals, students, newcomers and visitors. They use CitySeek to explore Darmstadt, find events and meet people. The app runs in German, English and Turkish.'],
   ['Can a guest use a coupon twice?', 'No. Each QR code works once. By default there is no limit, so users can get your coupon again whenever they have enough coins. If you prefer, each person gets one coupon per day, week or month.'],
   ['Can a user combine two coupons?', 'No. One coupon per payment. Coupons cannot be combined.'],
   ['Can I change or pause my offer?', 'Yes, any time. Tell us, and we change the offer, the days or the monthly number, or pause it.'],
   ['Is this only for Darmstadt?', 'We start in Darmstadt, where our community lives. If your place is somewhere else, write to us anyway.'],
   ['How does “bring a friend” work at the till?', 'The guest shows the coupon when ordering two of the same thing, for example two coffees. Your team scans it once and charges the second one at half price, or nothing, as agreed. If the prices differ, the cheaper one gets the discount.'],
  ] as Pair[],

  formLabel: 'Become a partner',
  formTitle: 'Tell us about your place.',
  formIntro: 'A few details are enough. We reply in person and set up your first coupon with you.',
  nextTitle: 'What happens next',
  next: [
   ['We get in touch', 'Usually within two working days, by email or phone.'],
   ['We set it up together', 'Your offer, your days, your monthly number. We can also drop by.'],
   ['You are on the map', 'Users see your coupon in the app and walk over.'],
  ] as Pair[],
  talk: 'Rather talk first? Write to',
  typeLegend: 'What kind of place do you run?',
  fieldBusiness: 'Name of your business',
  fieldAddress: 'Address (street, postcode, city)',
  fieldContact: 'Your name',
  fieldRole: 'Your role (optional)',
  rolePlaceholder: 'Owner, manager, marketing…',
  fieldEmail: 'Email',
  fieldPhone: 'Phone (optional)',
  fieldLink: 'Website or Instagram (optional)',
  offerLegend: 'Which offer would you like?',
  plans: { pair: ['Bring a friend', 'Second one half price or free'], standard: ['Come back again', '10% → 15% → 20% for the same person'], custom: ['My own offer', 'Something that fits my place'] },
  pairDealLegend: 'What does the second one cost?',
  pairDeals: { half: 'Half price', free: 'Free' },
  pairItemLabel: 'What counts? (optional)',
  pairItemPlaceholder: 'For example: any hot drink, or any main',
  sequenceLegend: 'Discount for the same person',
  sequences: { '10-15': ['10% → 15%', 'Then it stays at 15%.'], '10-15-20': ['10% → 15% → 20%', 'Then it stays at 20%.'], '10': ['10%', 'Fixed, every time.'] } as Record<Sequence, Pair>,
  sequenceHint: 'Their coupon only gets better after one was used at your place.',
  fixedHint: 'Every coupon is 10% off, whenever they have enough coins. It does not step up. If you choose a limit per person below, it still applies.',
  fixedCoinRule: 'CitySeek sets the coin price, in line with the other coupons on the map.',
  limitChoices: { none: 'No limit', day: 'Per day', week: 'Per week', month: 'Per month' } as Record<PerPersonLimit, string>,
  limitHints: { none: 'Users can get your coupon again whenever they have enough coins.', day: 'Each person gets one coupon per day.', week: 'Each person gets one coupon per week.', month: 'Each person gets one coupon per month.' } as Record<PerPersonLimit, string>,
  limitStepHints: { day: 'The better deal is waiting for them when the next day starts.', week: 'The better deal is waiting for them when the next week starts.', month: 'The better deal is waiting for them when the next month starts.' },
  customLabel: 'Your offer',
  customPlaceholder: 'For example: half price on the second drink, Monday to Thursday after 17:00',
  notesLabel: 'Days, times or anything else we should know (optional)',
  consent: 'CitySeek may contact me about this partnership using the details above.',
  privacy: 'Privacy policy',
  submit: 'Send partner request',
  sending: 'Sending…',
  formNote: 'Takes two minutes. No CitySeek account needed.',
  sentTitle: 'Thank you, your request is with us.',
  sentText: 'We will get back to you at {email} soon to talk about your offer and the next steps.',
  sentAgain: 'Send another request',
  sendError: 'Your request could not be sent. Your details are still here. Please try again or write to info@city-seek.com.',
  pickType: 'Please choose what kind of place you run.',

  finalTitle: 'Your street is already on our routes.',
  finalText: 'Let’s put your door on the map.',
  finalSecondary: 'See the coupon map',
  stickyCta: 'Become a partner',

  rights: 'All rights reserved.', where: 'Made in Darmstadt',
  legal: ['Imprint', 'Privacy', 'Terms', 'Cookies'],
 },

 de: {
  title: 'CitySeek Gutscheine für lokale Unternehmen in Darmstadt',
  description: 'Neue Gäste für Café, Bar, Restaurant oder Freizeitangebot in Darmstadt. Ohne Einrichtungsgebühr, ohne Vorauszahlung, ohne Werbebudget.',
  ogAlt: 'Gäste an der Tür des CitySeek Pop-ups in Darmstadt',
  skip: 'Zum Inhalt springen',
  menu: 'Menü', close: 'Schließen', languages: 'Sprache',
  nav: ['So funktioniert’s', 'Dein Angebot', 'Für wen', 'Fragen'],
  navCta: 'Partner werden',
  forEveryone: 'CitySeek für alle',

  heroLabel: 'CitySeek Gutscheine · für lokale Unternehmen',
  headline: ['Neue Gesichter', 'an deiner Theke.'],
  intro: 'CitySeek-Nutzer sammeln Coins auf Stadtspaziergängen und bei lokalen Events. Diese Coins tauschen sie gegen Coupons von Orten in der Stadt. Stell dein Angebot auf die Karte, dann kommen sie zu dir.',
  primary: 'Partner werden',
  secondary: 'So funktioniert’s',
  promises: ['Keine Einrichtungsgebühr', 'Keine Vorauszahlung', 'Kein Werbebudget', 'Jederzeit pausierbar'],
  heroPhotoAlt: 'Gäste an der Tür des CitySeek Pop-ups am Luisenplatz in Darmstadt',
  heroPhoneAlt: 'Die Coupon-Liste in der CitySeek App: Gratisgetränke und Kuchen in Darmstädter Cafés',
  scanTitle: 'Coupon gescannt',
  scanLine: 'Gratisgetränk · 1 neuer Gast',
  coinsChip: 'Verdient auf einem Stadtspaziergang',

  liveLabel: 'Schon auf der Coupon-Karte',
  liveCount: 'Gerade live: {c} Coupons an {p} Orten in Darmstadt',
  liveLink: 'Zur Coupon-Karte',

  whyLabel: 'Warum Orte mitmachen',
  whyTitle: 'Echte Menschen an deiner Tür, keine Klicks auf einem Bildschirm.',
  why: [
   ['Gäste statt Klicks', 'Jeder Coupon wird vor Ort eingelöst. Dein Team scannt an der Theke einen QR-Code, so siehst du jeden Besuch.'],
   ['Menschen, die schon unterwegs sind', 'Unsere Nutzer sind in der Stadt unterwegs: auf einem Spaziergang, auf dem Weg zu einem Event, auf der Suche nach dem nächsten Halt. Dein Laden liegt auf ihrem Weg.'],
   ['Aus Erstbesuchen werden Stammgäste', 'Bei „Wiederkommen“ wird derselbe Coupon für dieselbe Person besser. Das bessere Angebot gibt es erst, wenn sie einen Coupon bei dir eingelöst hat.'],
   ['Ruhige Stunden füllen', 'Du wählst Tage und Uhrzeiten. Kuchen am ruhigen Nachmittag, ein Getränk am Wochenanfang, ein Gratisspiel am stillen Dienstag.'],
   ['Du behältst die Kontrolle', 'Du bestimmst das Angebot, wie viele Coupons pro Monat laufen und wie lange. Ändern oder pausieren geht jederzeit.'],
   ['In drei Sprachen sichtbar', 'Dein Angebot erscheint auf Deutsch, Englisch und Türkisch. Studierende, Internationals und Neuzugezogene finden dich so leicht wie Einheimische.'],
  ] as Pair[],

  howLabel: 'So funktioniert’s',
  howTitle: 'Vom Stadtspaziergang an deine Theke',
  steps: [
   ['Sie erkunden', 'Nutzer laufen CitySeek-Routen durch Darmstadt, hören an jedem Halt eine Geschichte und kommen zu Community-Events.'],
   ['Sie sammeln Coins', 'Coins gibt es nur fürs Laufen und Mitmachen. Kaufen kann man sie nicht.'],
   ['Sie finden dich', 'Dein Coupon steht auf der CitySeek-Karte, mit Name, Adresse und Foto. Sie schalten ihn frei und kommen vorbei.'],
   ['Du scannst, fertig', 'Jede Handykamera liest den QR-Code. Ein Tipp bestätigt ihn. Kein Zusatzgerät, keine neue Kasse.'],
  ] as Pair[],

  seeLabel: 'Das sehen deine Gäste',
  seeTitle: 'Dein Laden genau dort, wo Leute ihren Tag planen.',
  seeText: 'CitySeek Gutscheine sind Teil der CitySeek App, neben Events und Stadtspaziergängen. Dort schauen die Leute nach, wenn sie überlegen, wohin es als Nächstes geht.',
  seePoints: [
   ['Ein Coupon mit deinem Namen', 'Dein Foto, deine Adresse und dein Angebot, auf einen Blick lesbar.'],
   ['Ein Pin auf der Coupon-Karte', 'Wer in der Nähe ist, sieht, wo du bist und wie weit es zu Fuß ist.'],
   ['Klare Farben', 'Gratis-Extras sind grün, Rabatte orange, Wiederkommen-Serien violett.'],
  ] as Pair[],
  shotList: 'Coupon-Liste',
  shotMap: 'Coupon-Karte',
  shotMapAlt: 'Die CitySeek Coupon-Karte von Darmstadt mit Partner-Pins',

  offerLabel: 'Dein Angebot',
  offerTitle: 'Drei Wege, neue Gäste willkommen zu heißen',
  offerIntro: 'Nimm eins unserer zwei Standardangebote oder erzähl uns deine eigene Idee. Du bestimmst, wie viele Coupons pro Monat laufen, und wir richten alles mit dir ein.',
  gainLabel: 'Was du davon hast',
  standardTag: 'Standard',
  pairName: 'Zu zweit',
  pairBadge: 'Ideal für kleine Beträge',
  pairVisual: [['1.', 'voller Preis'], ['2.', 'halber Preis']],
  pairText: 'Zwei Personen, ein Coupon. Sie bestellen zweimal dasselbe, zum Beispiel zwei Kaffees. Das zweite kostet die Hälfte, oder auf Wunsch gar nichts. Sind die Preise verschieden, gilt der Rabatt für das günstigere.',
  pairRules: ['Ein Coupon, eine Bestellung für zwei', 'Einmal gescannt, beim Bestellen', 'Du bestimmst, was zählt: Heißgetränke, Cocktails, Hauptgerichte oder Tickets', 'Du bestimmst Tage, Uhrzeiten und Monatszahl'],
  pairGain: 'Zwei Gäste für einen Coupon. Das erste Produkt wird voll bezahlt, und die Begleitung ist oft ein neues Gesicht.',
  seriesName: 'Wiederkommen',
  seriesBadge: 'Ideal fürs Essen',
  seriesLadder: [['10%', '1. Besuch', 'Der erste Coupon'], ['15%', 'Nach einem eingelösten Coupon', 'Der nächste Coupon'], ['20%', 'Danach', 'Es bleibt bei 20%']] as Triple[],
  seriesText: 'Derselbe Coupon wird für dieselbe Person besser. Beim ersten Mal zahlt sie mit 10% Rabatt. Nach diesem Besuch gibt ihr nächster Coupon 15%, dann 20%. Danach bleibt es bei 20%.',
  seriesShort: 'Oder zwei Stufen: 10% → 15%. Danach bleibt es bei 15%.',
  seriesFixed: 'Oder ganz einfach: feste 10%, jedes Mal.',
  seriesRules: ['Passt, wo Prozente echtes Geld sind: Essen, Bars, größere Rechnungen'],
  seriesGain: 'Ein erster Besuch und dann ein Grund wiederzukommen. So wird aus einem Fremden ein Stammgast.',
  ownTag: 'Individuell',
  ownName: 'Dein eigenes Angebot',
  ownText: 'Alles, was zu deinem Laden passt. Knüpf es an etwas, das gekauft wird, dann ist es an der Kasse immer klar.',
  ownExamples: ['Ein Keks gratis zu jedem Heißgetränk', 'Dessert gratis zum Hauptgericht', 'Kuchen zum halben Preis, Mo bis Do, 14 bis 17 Uhr', '4 spielen, 3 zahlen, unter der Woche', 'Die erste Stunde im Atelier gratis', 'Ein Topping oder Upgrade gratis'],
  ownGain: 'Ein Angebot, das zu deinen Preisen passt und die Stunden füllt, die du wählst.',
  tipTitle: 'Gut zu wissen',
  tipText: 'Bei einem Kaffee für 4 € sind 10% nur 40 Cent. Dafür läuft niemand durch die Stadt. Ein Extra gratis oder das zweite Getränk für die Begleitung wirkt viel größer und kostet dich trotzdem wenig. Prozente passen besser zu Essen und größeren Rechnungen.',
  exampleTitle: 'Beispiel: so hat ein Café es ausgefüllt',
  example: [['Angebot', 'Zu zweit: zweites Heißgetränk zum halben Preis'], ['Wann', 'Mo bis Do, 14 bis 17 Uhr'], ['Pro Monat', 'bis zu 400 Coupons'], ['Pro Person', '1 Coupon pro Monat']] as Pair[],

  limitLabel: 'Limit pro Person',
  limitTitle: 'Standardmäßig ohne Limit. Oder ein Coupon pro Tag, Woche oder Monat.',
  limitText: 'Kein Limit: Nutzer können deinen Coupon erneut holen, sobald sie genug Coins haben. Mit Limit bekommt jede Person einen Coupon pro Tag, Woche oder Monat.',
  limitScale: [['Kein Limit', 'Voreinstellung'], ['1 pro Person pro Tag', ''], ['1 pro Person pro Woche', ''], ['1 pro Person pro Monat', '']] as Pair[],
  limitSeries: 'Bei „Wiederkommen“ mit Stufen und Limit wartet das bessere Angebot auf sie, sobald der nächste Tag, die nächste Woche oder der nächste Monat beginnt.',
  comboRule: 'Ein Coupon pro Zahlung. Coupons lassen sich nicht kombinieren.',
  coinRule: 'CitySeek legt den Coin-Preis für jede Stufe fest, passend zu den anderen Coupons auf der Karte.',

  forLabel: 'Für wen',
  forTitle: 'Für Orte, in die man gern hineingeht',
  forHint: 'Tipp auf deins, um das Formular zu starten.',
  types: {
   cafe: ['Cafés und Bäckereien', 'Ein Kaffee, ein Gebäck, ein Stück Kuchen'],
   bar: ['Bars und Kneipen', 'Die erste Runde oder die Spezialität des Hauses'],
   restaurant: ['Restaurants', 'Eine Vorspeise, ein Dessert oder ein Mittagsangebot'],
   club: ['Clubs und Livemusik', 'Früher Gratis-Eintritt oder ein Begrüßungsdrink'],
   activity: ['Freizeit und Aktivitäten', 'Klettern, Escape Rooms, Bowling, Lasertag'],
   culture: ['Kultur und Führungen', 'Kino, Museen, Stadtführungen'],
   shop: ['Läden und Manufakturen', 'Buchläden, Concept Stores, lokale Marken'],
   sweets: ['Eis und Süßes', 'Eine Kugel, ein Baklava, etwas Süßes'],
   other: ['Etwas anderes', 'Erzähl uns von deinem Ort'],
  } as Record<BusinessType, Pair>,

  compareLabel: 'Im Vergleich zu anderen Kanälen',
  compareTitle: 'Der Kanal, der Gäste bringt statt Reichweite',
  compareCols: ['Liefer-Apps', 'Deal-Portale', 'Online-Werbung', 'CitySeek'],
  compareRows: [
   ['Gäste kommen zu dir in den Laden', 'no', 'yes', 'partly', 'yes'],
   ['Jeder Besuch wird an der Theke bestätigt', 'no', 'partly', 'no', 'yes'],
   ['Du legst ein Monatslimit fest', 'no', 'partly', 'yes', 'yes'],
   ['Belohnt denselben Gast fürs Wiederkommen', 'no', 'no', 'partly', 'yes'],
   ['Erreicht Menschen, die schon in der Stadt unterwegs sind', 'no', 'no', 'partly', 'yes'],
  ] as [string, string, string, string, string][],
  compareWords: { yes: 'Ja', partly: 'Teils', no: 'Nein' },
  compareNote: 'Teils heißt: möglich mit Zusatzaufwand oder zusätzlichen Kosten, aber nicht eingebaut.',

  communityLabel: 'Mehr als ein Coupon',
  communityTitle: 'Wir kommen gemeinsam vorbei.',
  communityText: 'CitySeek ist zuerst eine Community. Bei unseren Meetups und Spaziergängen besuchen wir Partnerorte als Gruppe. So wird ein neuer Laden schnell zum gemeinsamen Lieblingsort. Wir machen Livemusik, Frühstücke und Pop-ups in ganz Darmstadt, und wir bringen Leute mit.',
  stats: [[COUPON_STATS.events, 'Community-Events in Darmstadt'], [COUPON_STATS.visits, 'Besuche bei unseren Events'], [COUPON_STATS.places, 'Orte mit Geschichte auf unseren Routen'], [COUPON_STATS.languages, 'Sprachen: Deutsch, English, Türkçe']] as Pair[],
  gardenAlt: 'Livemusik mit Saz und Cello bei einem CitySeek-Event in einem Gewächshaus',
  insideAlt: 'Im CitySeek Pop-up am Luisenplatz',

  faqTitle: 'Was Orte uns oft fragen',
  faqs: [
   ['Was kostet das?', 'Keine Einrichtungsgebühr, keine Vorauszahlung, kein Werbebudget. Bevor dein Coupon live geht, vereinbaren wir mit dir persönlich einfache und faire Bedingungen. Du bestimmst, wie viele Coupons pro Monat laufen, also gibt es keine Überraschungen.'],
   ['Braucht mein Team eine App oder ein neues Gerät?', 'Nein. Jedes Handy mit Kamera liest den QR-Code und öffnet den Coupon. Ein Tipp markiert ihn als eingelöst.'],
   ['Wer nutzt CitySeek?', 'Einheimische, Studierende, Neuzugezogene und Besucher. Sie nutzen CitySeek, um Darmstadt zu entdecken, Events zu finden und Leute kennenzulernen. Die App gibt es auf Deutsch, Englisch und Türkisch.'],
   ['Kann ein Gast einen Coupon zweimal nutzen?', 'Nein. Jeder QR-Code funktioniert genau einmal. Standardmäßig gibt es kein Limit, also können Nutzer deinen Coupon erneut holen, sobald sie genug Coins haben. Wenn du willst, bekommt jede Person einen Coupon pro Tag, Woche oder Monat.'],
   ['Kann ein Nutzer zwei Coupons kombinieren?', 'Nein. Ein Coupon pro Zahlung. Coupons lassen sich nicht kombinieren.'],
   ['Kann ich mein Angebot ändern oder pausieren?', 'Ja, jederzeit. Sag uns Bescheid, dann ändern wir Angebot, Tage oder Monatszahl, oder wir pausieren es.'],
   ['Gibt es das nur in Darmstadt?', 'Wir starten in Darmstadt, wo unsere Community zu Hause ist. Wenn dein Laden woanders ist, schreib uns trotzdem.'],
   ['Wie läuft „Zu zweit“ an der Kasse?', 'Der Gast zeigt den Coupon, wenn er zweimal dasselbe bestellt, zum Beispiel zwei Kaffees. Dein Team scannt ihn einmal und berechnet das zweite zum halben Preis oder gar nicht, wie vereinbart. Sind die Preise verschieden, gilt der Rabatt für das günstigere.'],
  ] as Pair[],

  formLabel: 'Partner werden',
  formTitle: 'Erzähl uns von deinem Laden.',
  formIntro: 'Ein paar Angaben reichen. Wir melden uns persönlich und richten deinen ersten Coupon mit dir ein.',
  nextTitle: 'So geht es weiter',
  next: [
   ['Wir melden uns', 'Meist innerhalb von zwei Werktagen, per E-Mail oder Telefon.'],
   ['Wir richten es gemeinsam ein', 'Dein Angebot, deine Tage, deine Monatszahl. Gern kommen wir auch vorbei.'],
   ['Du bist auf der Karte', 'Nutzer sehen deinen Coupon in der App und kommen vorbei.'],
  ] as Pair[],
  talk: 'Lieber erst reden? Schreib an',
  typeLegend: 'Was für einen Ort hast du?',
  fieldBusiness: 'Name deines Unternehmens',
  fieldAddress: 'Adresse (Straße, PLZ, Ort)',
  fieldContact: 'Dein Name',
  fieldRole: 'Deine Rolle (optional)',
  rolePlaceholder: 'Inhaber, Leitung, Marketing…',
  fieldEmail: 'E-Mail',
  fieldPhone: 'Telefon (optional)',
  fieldLink: 'Website oder Instagram (optional)',
  offerLegend: 'Welches Angebot möchtest du?',
  plans: { pair: ['Zu zweit', 'Das zweite zum halben Preis oder gratis'], standard: ['Wiederkommen', '10% → 15% → 20% für dieselbe Person'], custom: ['Mein eigenes Angebot', 'Etwas, das zu meinem Laden passt'] },
  pairDealLegend: 'Was kostet das zweite?',
  pairDeals: { half: 'Halber Preis', free: 'Gratis' },
  pairItemLabel: 'Was zählt? (optional)',
  pairItemPlaceholder: 'Zum Beispiel: jedes Heißgetränk oder jedes Hauptgericht',
  sequenceLegend: 'Rabatt für dieselbe Person',
  sequences: { '10-15': ['10% → 15%', 'Danach bleibt es bei 15%.'], '10-15-20': ['10% → 15% → 20%', 'Danach bleibt es bei 20%.'], '10': ['10%', 'Jedes Mal gleich.'] } as Record<Sequence, Pair>,
  sequenceHint: 'Der Coupon wird erst besser, wenn einer bei dir eingelöst wurde.',
  fixedHint: 'Jeder Coupon gibt 10% Rabatt, sooft sie genug Coins haben. Es gibt keine Stufen. Wählst du unten ein Limit pro Person, gilt es trotzdem.',
  fixedCoinRule: 'CitySeek legt den Coin-Preis fest, passend zu den anderen Coupons auf der Karte.',
  limitChoices: { none: 'Kein Limit', day: 'Pro Tag', week: 'Pro Woche', month: 'Pro Monat' } as Record<PerPersonLimit, string>,
  limitHints: { none: 'Nutzer können deinen Coupon erneut holen, sobald sie genug Coins haben.', day: 'Jede Person bekommt einen Coupon pro Tag.', week: 'Jede Person bekommt einen Coupon pro Woche.', month: 'Jede Person bekommt einen Coupon pro Monat.' } as Record<PerPersonLimit, string>,
  limitStepHints: { day: 'Das bessere Angebot wartet auf sie, sobald der nächste Tag beginnt.', week: 'Das bessere Angebot wartet auf sie, sobald die nächste Woche beginnt.', month: 'Das bessere Angebot wartet auf sie, sobald der nächste Monat beginnt.' },
  customLabel: 'Dein Angebot',
  customPlaceholder: 'Zum Beispiel: das zweite Getränk zum halben Preis, montags bis donnerstags ab 17 Uhr',
  notesLabel: 'Tage, Uhrzeiten oder sonst etwas, das wir wissen sollten (optional)',
  consent: 'CitySeek darf mich über die angegebenen Kontaktdaten zu dieser Partnerschaft kontaktieren.',
  privacy: 'Datenschutzerklärung',
  submit: 'Partneranfrage senden',
  sending: 'Wird gesendet…',
  formNote: 'Dauert zwei Minuten. Kein CitySeek-Konto nötig.',
  sentTitle: 'Danke, deine Anfrage ist bei uns.',
  sentText: 'Wir melden uns bald unter {email}, um dein Angebot und die nächsten Schritte zu besprechen.',
  sentAgain: 'Noch eine Anfrage senden',
  sendError: 'Deine Anfrage konnte nicht gesendet werden. Deine Angaben sind noch da. Bitte versuch es noch einmal oder schreib an info@city-seek.com.',
  pickType: 'Bitte wähle aus, was für einen Ort du hast.',

  finalTitle: 'Deine Straße liegt schon auf unseren Routen.',
  finalText: 'Bringen wir deine Tür auf die Karte.',
  finalSecondary: 'Zur Coupon-Karte',
  stickyCta: 'Partner werden',

  rights: 'Alle Rechte vorbehalten.', where: 'Gemacht in Darmstadt',
  legal: ['Impressum', 'Datenschutz', 'AGB', 'Cookies'],
 },

 tr: {
  title: 'Darmstadt’ta yerel işletmeler için CitySeek Kuponları',
  description: 'Darmstadt’ta kafene, barına, restoranına ya da aktivitene yeni misafirler. Kurulum ücreti yok, ön ödeme yok, reklam bütçesi yok.',
  ogAlt: 'Darmstadt’taki CitySeek pop-up’ının kapısında misafirler',
  skip: 'İçeriğe geç',
  menu: 'Menü', close: 'Kapat', languages: 'Dil',
  nav: ['Nasıl çalışır', 'Teklifin', 'Kimler için', 'Sorular'],
  navCta: 'Partner ol',
  forEveryone: 'Herkes için CitySeek',

  heroLabel: 'CitySeek Kuponları · yerel işletmeler için',
  headline: ['Tezgâhında', 'yeni yüzler.'],
  intro: 'CitySeek kullanıcıları şehir yürüyüşlerinde ve yerel etkinliklerde coin kazanıyor. Bu coinleri şehirdeki mekânların kuponlarıyla değiştiriyorlar. Teklifini haritaya koy, onlar da sana gelsin.',
  primary: 'Partner ol',
  secondary: 'Nasıl çalıştığını gör',
  promises: ['Kurulum ücreti yok', 'Ön ödeme yok', 'Reklam bütçesi yok', 'İstediğin zaman durdur'],
  heroPhotoAlt: 'Darmstadt Luisenplatz’taki CitySeek pop-up’ının kapısında misafirler',
  heroPhoneAlt: 'CitySeek uygulamasında kupon listesi: Darmstadt kafelerinde ücretsiz içecek ve pasta',
  scanTitle: 'Kupon okutuldu',
  scanLine: 'Ücretsiz içecek · 1 misafir geldi',
  coinsChip: 'Bir şehir yürüyüşünde kazanıldı',

  liveLabel: 'Kupon haritasında olanlar',
  liveCount: 'Şu an yayında: Darmstadt’ta {p} mekânda {c} kupon',
  liveLink: 'Kupon haritasını aç',

  whyLabel: 'Mekânlar neden katılıyor',
  whyTitle: 'Ekranda tıklama değil, kapından giren gerçek insanlar.',
  why: [
   ['Tıklama değil, misafir', 'Her kupon yerinde kullanılır. Ekibin tezgâhta bir QR kodu okutur, böylece her ziyareti görürsün.'],
   ['Zaten dışarıda olan insanlar', 'Kullanıcılarımız şehirde: bir yürüyüşte, bir etkinliğe giderken ya da sıradaki durağı ararken. Mekânın tam yollarının üstünde.'],
   ['İlk ziyaretten müdavimliğe', '“Tekrar gel” ile aynı kupon, aynı kişi için daha iyi hale gelir. Daha iyi teklif, kişi sende bir kupon kullandıktan sonra gelir.'],
   ['Sakin saatler dolsun', 'Günleri ve saatleri sen seçersin. Sakin bir öğleden sonra pasta, hafta başında bir içecek, sessiz bir salı günü ücretsiz bir oyun.'],
   ['Kontrol sende', 'Teklifi, ayda kaç kupon olacağını ve ne kadar süreceğini sen belirlersin. İstediğin zaman değiştir ya da durdur.'],
   ['Üç dilde görünür', 'Teklifin Almanca, İngilizce ve Türkçe görünür. Öğrenciler, yabancılar ve şehre yeni gelenler seni yerliler kadar kolay bulur.'],
  ] as Pair[],

  howLabel: 'Nasıl çalışır',
  howTitle: 'Şehir yürüyüşünden tezgâhına',
  steps: [
   ['Keşfediyorlar', 'Kullanıcılar Darmstadt’ta CitySeek rotalarını yürüyor, her durakta bir hikâye dinliyor ve topluluk etkinliklerine katılıyor.'],
   ['Coin kazanıyorlar', 'Coin sadece yürüyerek ve katılarak kazanılır. Satın alınamaz.'],
   ['Seni buluyorlar', 'Kuponun adın, adresin ve fotoğrafınla CitySeek haritasında. Kuponu açıp sana geliyorlar.'],
   ['Okut, bitti', 'Herhangi bir telefon kamerası QR kodu okur. Tek dokunuşla onaylanır. Ek cihaz yok, yeni kasa yok.'],
  ] as Pair[],

  seeLabel: 'Misafirlerinin gördüğü',
  seeTitle: 'Mekânın, insanların gününü planladığı yerde.',
  seeText: 'CitySeek Kuponları, CitySeek uygulamasında etkinliklerin ve şehir yürüyüşlerinin yanında yer alıyor. İnsanlar bir sonraki durağa karar verirken buraya bakıyor.',
  seePoints: [
   ['Adının yazdığı bir kupon', 'Fotoğrafın, adresin ve teklifin, bir bakışta okunur.'],
   ['Kupon haritasında bir iğne', 'Yakındaki herkes nerede olduğunu ve yürüyerek ne kadar uzakta olduğunu görür.'],
   ['Net renkler', 'Ücretsiz ikramlar yeşil, indirimler turuncu, tekrar ziyaret serileri mor.'],
  ] as Pair[],
  shotList: 'Kupon listesi',
  shotMap: 'Kupon haritası',
  shotMapAlt: 'Partner iğneleriyle Darmstadt’ın CitySeek kupon haritası',

  offerLabel: 'Teklifin',
  offerTitle: 'Yeni misafirleri ağırlamanın üç yolu',
  offerIntro: 'İki standart teklifimizden birini seç ya da kendi fikrini anlat. Ayda kaç kupon olacağına sen karar verirsin, kurulumu seninle birlikte yaparız.',
  gainLabel: 'Sana ne kazandırır',
  standardTag: 'Standart',
  pairName: 'Arkadaşınla gel',
  pairBadge: 'Küçük sepetler için ideal',
  pairVisual: [['1.', 'tam fiyat'], ['2.', 'yarı fiyat']],
  pairText: 'İki kişi, tek kupon. Aynı şeyden iki tane sipariş ederler, örneğin iki kahve. İkincisi yarı fiyatına, istersen bedava. Fiyatlar farklıysa indirim ucuz olana uygulanır.',
  pairRules: ['Tek kupon, iki kişilik tek sipariş', 'Sipariş verirken bir kez okutulur', 'Neyin geçerli olduğunu sen seçersin: sıcak içecek, kokteyl, ana yemek ya da bilet', 'Günleri, saatleri ve aylık sayıyı sen belirlersin'],
  pairGain: 'Tek kupona iki misafir. İlk ürün tam fiyatına ödenir ve yanındaki arkadaş çoğu zaman yeni bir yüzdür.',
  seriesName: 'Tekrar gel',
  seriesBadge: 'Yemek için ideal',
  seriesLadder: [['%10', '1. ziyaret', 'İlk kupon'], ['%15', 'Kullanılmış bir kupondan sonra', 'Sonraki kupon'], ['%20', 'Sonra', 'Hep %20’de kalır']] as Triple[],
  seriesText: 'Aynı kupon, aynı kişi için daha iyi hale gelir. İlk seferde %10 indirimle öder. Bu ziyaretten sonra bir sonraki kuponu %15, ardından %20 olur. Bundan sonra hep %20’de kalır.',
  seriesShort: 'Ya da iki adım: %10 → %15. Bundan sonra hep %15’te kalır.',
  seriesFixed: 'Ya da en basiti: her seferinde sabit %10.',
  seriesRules: ['Yüzdenin gerçek para ettiği yerlerde iyi çalışır: yemek, bar, büyük hesaplar'],
  seriesGain: 'Önce ilk ziyaret, sonra geri gelmek için bir sebep. Yabancı bir misafir böyle müdavim olur.',
  ownTag: 'Sana özel',
  ownName: 'Kendi teklifin',
  ownText: 'Mekânına ne uyuyorsa. Satın alınan bir şeye bağla, böylece kasada her zaman net olsun.',
  ownExamples: ['Her sıcak içeceğe bir kurabiye hediye', 'Ana yemeğe tatlı hediye', 'Pazartesiden perşembeye 14 ile 17 arası pasta yarı fiyatına', 'Hafta içi 4 kişi oynar, 3 kişi öder', 'Atölyede ilk saat ücretsiz', 'Ücretsiz bir ekstra ya da büyük boy'],
  ownGain: 'Fiyatlarına uyan ve seçtiğin saatleri dolduran bir teklif.',
  tipTitle: 'Bilmekte fayda var',
  tipText: '4 €’luk bir kahvede %10 sadece 40 sent eder. Kimse bunun için şehrin öbür ucundan gelmez. Ücretsiz bir ekstra ya da arkadaşa ikinci içecek çok daha büyük görünür ve sana yine de az maliyetli olur. Yüzdeler yemeklerde ve büyük hesaplarda daha iyi çalışır.',
  exampleTitle: 'Örnek: bir kafe böyle doldurdu',
  example: [['Teklif', 'Arkadaşınla gel: ikinci sıcak içecek yarı fiyatına'], ['Ne zaman', 'Pazartesi ile perşembe arası, 14.00 ile 17.00'], ['Ayda', 'en fazla 400 kupon'], ['Kişi başı', 'ayda 1 kupon']] as Pair[],

  limitLabel: 'Kişi başı sınır',
  limitTitle: 'Varsayılan olarak sınır yok. İstersen günde, haftada ya da ayda bir kupon.',
  limitText: 'Sınır yok: kullanıcılar yeterli coinleri olduğunda kuponunu yeniden alabilir. Sınır koyarsan her kişi günde, haftada ya da ayda tek kupon alır.',
  limitScale: [['Sınır yok', 'Varsayılan'], ['Kişi başı günde 1', ''], ['Kişi başı haftada 1', ''], ['Kişi başı ayda 1', '']] as Pair[],
  limitSeries: '“Tekrar gel” teklifinde adımlar ve sınır varsa, daha iyi teklif onları yeni gün, hafta ya da ay başladığında bekler.',
  comboRule: 'Her ödemede tek kupon. Kuponlar birleştirilemez.',
  coinRule: 'Her adımın coin fiyatını, haritadaki diğer kuponlarla uyumlu olacak şekilde CitySeek belirler.',

  forLabel: 'Kimler için',
  forTitle: 'İnsanların içeri girmeyi sevdiği yerler için',
  forHint: 'Formu başlatmak için seninkine dokun.',
  types: {
   cafe: ['Kafeler ve fırınlar', 'Bir kahve, bir hamur işi, bir dilim pasta'],
   bar: ['Barlar ve pub’lar', 'İlk tur ya da evin özel içeceği'],
   restaurant: ['Restoranlar', 'Bir başlangıç, bir tatlı ya da öğle menüsü'],
   club: ['Kulüpler ve canlı müzik', 'Erken saatte ücretsiz giriş ya da karşılama içeceği'],
   activity: ['Aktiviteler', 'Tırmanış, kaçış odaları, bowling, lazer tag'],
   culture: ['Kültür ve turlar', 'Sinema, müzeler, rehberli yürüyüşler'],
   shop: ['Dükkânlar ve üreticiler', 'Kitapçılar, konsept mağazalar, yerel markalar'],
   sweets: ['Dondurma ve tatlı', 'Bir top dondurma, bir baklava, tatlı bir şey'],
   other: ['Başka bir şey', 'Bize mekânını anlat'],
  } as Record<BusinessType, Pair>,

  compareLabel: 'Diğer kanallarla karşılaştırınca',
  compareTitle: 'Erişim değil, misafir getiren kanal',
  compareCols: ['Yemek siparişi uygulamaları', 'Fırsat siteleri', 'Online reklam', 'CitySeek'],
  compareRows: [
   ['Misafir mekânına gelir', 'no', 'yes', 'partly', 'yes'],
   ['Her ziyaret tezgâhta onaylanır', 'no', 'partly', 'no', 'yes'],
   ['Aylık sınırı sen belirlersin', 'no', 'partly', 'yes', 'yes'],
   ['Aynı misafiri geri geldiği için ödüllendirir', 'no', 'no', 'partly', 'yes'],
   ['Zaten şehirde dolaşan insanlara ulaşır', 'no', 'no', 'partly', 'yes'],
  ] as [string, string, string, string, string][],
  compareWords: { yes: 'Evet', partly: 'Kısmen', no: 'Hayır' },
  compareNote: 'Kısmen: ek emek ya da ek harcamayla mümkün, ama hazır gelmiyor.',

  communityLabel: 'Bir kupondan fazlası',
  communityTitle: 'Sana birlikte geliyoruz.',
  communityText: 'CitySeek her şeyden önce bir topluluk. Buluşmalarımızda ve yürüyüşlerimizde partner mekânlara grup olarak gidiyoruz. Böylece yeni bir yer kısa sürede ortak favorimiz oluyor. Darmstadt’ın her yerinde canlı müzik, kahvaltılar ve pop-up’lar düzenliyoruz ve insanları da getiriyoruz.',
  stats: [[COUPON_STATS.events, 'Darmstadt’ta topluluk etkinliği'], [COUPON_STATS.visits, 'etkinliklerimize katılım'], [COUPON_STATS.places, 'rotalarımızda hikâyesi olan yer'], [COUPON_STATS.languages, 'dil: Deutsch, English, Türkçe']] as Pair[],
  gardenAlt: 'Bir CitySeek etkinliğinde serada saz ve çelloyla canlı müzik',
  insideAlt: 'Luisenplatz’taki CitySeek pop-up’ının içi',

  faqTitle: 'Mekânların bize en çok sorduğu sorular',
  faqs: [
   ['Ücreti ne kadar?', 'Kurulum ücreti yok, ön ödeme yok, reklam bütçesi yok. Kuponun yayına girmeden önce seninle yüz yüze basit ve adil koşullarda anlaşıyoruz. Ayda kaç kupon olacağını sen belirliyorsun, yani sürpriz yok.'],
   ['Ekibimin bir uygulamaya ya da yeni bir cihaza ihtiyacı var mı?', 'Hayır. Kamerası olan her telefon QR kodu okur ve kuponu açar. Tek dokunuşla kullanıldı olarak işaretlenir.'],
   ['CitySeek’i kimler kullanıyor?', 'Yerliler, öğrenciler, şehre yeni gelenler ve ziyaretçiler. CitySeek ile Darmstadt’ı keşfediyor, etkinlik buluyor ve yeni insanlarla tanışıyorlar. Uygulama Almanca, İngilizce ve Türkçe.'],
   ['Bir misafir aynı kuponu iki kez kullanabilir mi?', 'Hayır. Her QR kod yalnızca bir kez çalışır. Varsayılan olarak sınır yoktur, yani kullanıcılar yeterli coinleri olduğunda kuponunu yeniden alabilir. İstersen her kişi günde, haftada ya da ayda tek kupon alır.'],
   ['Bir kullanıcı iki kuponu birleştirebilir mi?', 'Hayır. Her ödemede tek kupon kullanılır. Kuponlar birleştirilemez.'],
   ['Teklifimi değiştirebilir ya da durdurabilir miyim?', 'Evet, istediğin zaman. Bize haber ver; teklifi, günleri ya da aylık sayıyı değiştirelim veya durduralım.'],
   ['Bu sadece Darmstadt’ta mı var?', 'Topluluğumuzun yaşadığı Darmstadt’ta başlıyoruz. Mekânın başka bir şehirdeyse yine de bize yaz.'],
   ['“Arkadaşınla gel” kasada nasıl işliyor?', 'Misafir aynı şeyden iki tane sipariş ederken, örneğin iki kahve, kuponu gösterir. Ekibin kuponu bir kez okutur ve ikincisini anlaştığınız gibi yarı fiyatına ya da ücretsiz hesaplar. Fiyatlar farklıysa indirim ucuz olana uygulanır.'],
  ] as Pair[],

  formLabel: 'Partner ol',
  formTitle: 'Bize mekânını anlat.',
  formIntro: 'Birkaç bilgi yeterli. Sana bizzat dönüyoruz ve ilk kuponunu seninle birlikte hazırlıyoruz.',
  nextTitle: 'Sonra ne oluyor',
  next: [
   ['Sana ulaşıyoruz', 'Genellikle iki iş günü içinde, e-posta ya da telefonla.'],
   ['Birlikte kuruyoruz', 'Teklifin, günlerin, aylık sayın. İstersen uğrayabiliriz de.'],
   ['Haritadasın', 'Kullanıcılar kuponunu uygulamada görür ve sana gelir.'],
  ] as Pair[],
  talk: 'Önce konuşmak mı istersin? Yaz:',
  typeLegend: 'Nasıl bir mekân işletiyorsun?',
  fieldBusiness: 'İşletmenin adı',
  fieldAddress: 'Adres (sokak, posta kodu, şehir)',
  fieldContact: 'Adın',
  fieldRole: 'Görevin (isteğe bağlı)',
  rolePlaceholder: 'Sahibi, müdür, pazarlama…',
  fieldEmail: 'E-posta',
  fieldPhone: 'Telefon (isteğe bağlı)',
  fieldLink: 'Web sitesi ya da Instagram (isteğe bağlı)',
  offerLegend: 'Hangi teklifi istersin?',
  plans: { pair: ['Arkadaşınla gel', 'İkincisi yarı fiyatına ya da bedava'], standard: ['Tekrar gel', 'Aynı kişiye %10 → %15 → %20'], custom: ['Kendi teklifim', 'Mekânıma uyan bir şey'] },
  pairDealLegend: 'İkincisi ne kadar olsun?',
  pairDeals: { half: 'Yarı fiyat', free: 'Bedava' },
  pairItemLabel: 'Neler geçerli? (isteğe bağlı)',
  pairItemPlaceholder: 'Örneğin: tüm sıcak içecekler ya da tüm ana yemekler',
  sequenceLegend: 'Aynı kişi için indirim',
  sequences: { '10-15': ['%10 → %15', 'Sonra hep %15’te kalır.'], '10-15-20': ['%10 → %15 → %20', 'Sonra hep %20’de kalır.'], '10': ['%10', 'Her seferinde sabit.'] } as Record<Sequence, Pair>,
  sequenceHint: 'Kupon ancak sende bir kupon kullanıldıktan sonra daha iyi olur.',
  fixedHint: 'Yeterli coinleri olduğu sürece her kupon %10 indirimlidir. İndirim artmaz. Aşağıda kişi başı bir sınır seçersen, o sınır yine geçerlidir.',
  fixedCoinRule: 'Coin fiyatını, haritadaki diğer kuponlarla uyumlu olacak şekilde CitySeek belirler.',
  limitChoices: { none: 'Sınır yok', day: 'Günde 1', week: 'Haftada 1', month: 'Ayda 1' } as Record<PerPersonLimit, string>,
  limitHints: { none: 'Kullanıcılar yeterli coinleri olduğunda kuponunu yeniden alabilir.', day: 'Her kişi günde tek kupon alır.', week: 'Her kişi haftada tek kupon alır.', month: 'Her kişi ayda tek kupon alır.' } as Record<PerPersonLimit, string>,
  limitStepHints: { day: 'Daha iyi teklif, yeni gün başladığında onları bekler.', week: 'Daha iyi teklif, yeni hafta başladığında onları bekler.', month: 'Daha iyi teklif, yeni ay başladığında onları bekler.' },
  customLabel: 'Teklifin',
  customPlaceholder: 'Örneğin: pazartesiden perşembeye saat 17.00’den sonra ikinci içecek yarı fiyatına',
  notesLabel: 'Günler, saatler ya da bilmemiz gereken başka bir şey (isteğe bağlı)',
  consent: 'CitySeek bu ortaklık için yukarıdaki bilgilerle benimle iletişime geçebilir.',
  privacy: 'Gizlilik politikası',
  submit: 'Partner talebini gönder',
  sending: 'Gönderiliyor…',
  formNote: 'İki dakika sürer. CitySeek hesabı gerekmez.',
  sentTitle: 'Teşekkürler, talebin bize ulaştı.',
  sentText: 'Teklifini ve sonraki adımları konuşmak için yakında {email} adresinden sana döneceğiz.',
  sentAgain: 'Bir talep daha gönder',
  sendError: 'Talebin gönderilemedi. Bilgilerin hâlâ burada. Lütfen tekrar dene ya da info@city-seek.com adresine yaz.',
  pickType: 'Lütfen nasıl bir mekân işlettiğini seç.',

  finalTitle: 'Sokağın zaten rotalarımızın üstünde.',
  finalText: 'Hadi kapını haritaya taşıyalım.',
  finalSecondary: 'Kupon haritasını gör',
  stickyCta: 'Partner ol',

  rights: 'Tüm hakları saklıdır.', where: 'Darmstadt’ta yapıldı',
  legal: ['Künye', 'Gizlilik', 'Koşullar', 'Çerezler'],
 },
};

export type CouponsCopy = (typeof couponsCopy)[Language];
